import type { UiVerdict } from '../services/api';

export type FilterToggles = {
  bds: { enabled: boolean; categories?: undefined };
  defense: boolean;
  surveillance: boolean;
  shariah: boolean;
};

export type ScreeningPresetId =
  | 'muslim_investor'
  | 'occupation_focus'
  | 'anti_war'
  | 'full_scan';

export interface ScreeningPreset {
  id: ScreeningPresetId;
  label: string;
  description: string;
  toggles: FilterToggles;
}

export const SCREENING_PRESETS: ScreeningPreset[] = [
  {
    id: 'muslim_investor',
    label: 'Muslim investor',
    description: 'Halal screening (Shariah rules). Occupation and defense stay off unless you toggle them.',
    toggles: {
      bds: { enabled: false },
      defense: false,
      surveillance: false,
      shariah: true,
    },
  },
  {
    id: 'occupation_focus',
    label: 'Occupation focus',
    description: 'Settlement and occupation list evidence only (AFSC, Who Profits).',
    toggles: {
      bds: { enabled: true },
      defense: false,
      surveillance: false,
      shariah: false,
    },
  },
  {
    id: 'anti_war',
    label: 'Anti-war',
    description: 'Occupation & settlements plus defense contractors (SIPRI, DoD contracts).',
    toggles: {
      bds: { enabled: true },
      defense: true,
      surveillance: false,
      shariah: false,
    },
  },
  {
    id: 'full_scan',
    label: 'Full scan',
    description: 'All lenses: occupation & settlements, defense, and Shariah.',
    toggles: {
      bds: { enabled: true },
      defense: true,
      surveillance: false,
      shariah: true,
    },
  },
];

export function togglesMatchPreset(
  toggles: FilterToggles,
  preset: ScreeningPreset
): boolean {
  return (
    toggles.bds.enabled === preset.toggles.bds.enabled &&
    toggles.defense === preset.toggles.defense &&
    toggles.surveillance === preset.toggles.surveillance &&
    toggles.shariah === preset.toggles.shariah
  );
}

export function findActivePreset(toggles: FilterToggles): ScreeningPresetId | null {
  const match = SCREENING_PRESETS.find((p) => togglesMatchPreset(toggles, p));
  return match?.id ?? null;
}

export function isNotInDatabaseReasons(reasons: string[] | null | undefined): boolean {
  if (!reasons?.length) return false;
  return reasons.some((r) => r.includes('not in the EthicCheck database'));
}

export type VerdictKind = UiVerdict;

export function summarizeResults(
  results: Array<{ verdict: UiVerdict; reasons: string[] | null | undefined }>
): {
  clean: number;
  review: number;
  excluded: number;
  notInDatabase: number;
  total: number;
} {
  let clean = 0;
  let review = 0;
  let excluded = 0;
  let notInDatabase = 0;

  for (const row of results) {
    if (isNotInDatabaseReasons(row.reasons)) notInDatabase++;
    if (row.verdict === 'clean') clean++;
    else if (row.verdict === 'review') review++;
    else if (row.verdict === 'block') excluded++;
  }

  return { clean, review, excluded, notInDatabase, total: results.length };
}
